import type { DialogProps } from '@mui/material';
import { Box, Dialog, IconButton } from '@mui/material';
import { styled } from '@mui/material/styles';
import type { StyledComponent } from '@mui/styled-engine';

/**
 * Props for StyledDialog
 */
interface StyledDialogProps extends DialogProps {
  variantColor?: string;
}

/**
 * Styled Dialog with colored left border
 * Background is always white
 * Position relative for absolute close button
 */
export const StyledDialog: StyledComponent<StyledDialogProps> = styled(Dialog, {
  shouldForwardProp: prop => prop !== 'variantColor',
})<{ variantColor?: string }>(({ variantColor }) => ({
  '& .MuiDialog-paper': {
    position: 'relative', // For absolute close button positioning
    backgroundColor: '#ffffff', // Always white background
    borderLeft: variantColor ? `12px solid ${variantColor}` : 'none', // Colored left border
    borderRadius: '8px',
    boxShadow: '0 8px 24px rgba(0, 0, 0, 0.15)', // Subtle shadow
  },
}));

/**
 * Absolutely positioned close button at top-right
 */
export const CloseButton = styled(IconButton)({
  position: 'absolute',
  top: '12px',
  right: '12px',
  zIndex: 1,
}) as typeof IconButton;

/**
 * Main content wrapper with two-column layout
 * Icon column + Content column
 */
export const ContentWrapper = styled(Box)({
  display: 'flex',
  gap: '16px',
  padding: '24px 24px 16px 24px', // Top, left/right, bottom
}) as typeof Box;

/**
 * Icon column (left side)
 */
export const IconColumn = styled(Box)({
  flexShrink: 0,
  display: 'flex',
  alignItems: 'flex-start',
}) as typeof Box;

/**
 * Content column (right side) - contains title and message
 */
export const ContentColumn = styled(Box)({
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  gap: '8px',
}) as typeof Box;
